import React, { createContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AuthContextProps, AuthProviderProps, AuthContextState, AuthTokenData, LoginData, LogoutData, User } from "../interfaces";
import { fetchCsrfToken, getDeviceId, getEmail, getIdApp, getInviteKey, referer } from "../utils";
import { generateRequestConfig, projectApiUrl } from "../constants";

const emptyUser: User = { email: null, name: null };

// docs/security-and-platform-work-plan.md Этап 7 — access/refresh tokens
// are now HttpOnly cookies the backend itself sets/reads; this frontend
// never sees their values and never writes them. "Am I logged in, and as
// whom" now comes from asking this app's own backend (/me) instead of
// decoding a JWT the browser can no longer read.
type MeResponse = { userId: string; email: string | null; displayName: string | null };

const fetchMe = async (): Promise<MeResponse | null> => {
  if (!projectApiUrl) return null;
  try {
    const response = await axios.get(`${projectApiUrl.replace(/\/$/, "")}/me`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      withCredentials: true,
    });
    return response.data as MeResponse;
  } catch {
    return null;
  }
};

const initialAuthContextState: AuthContextState = {
  user: emptyUser,
  csrf_token: null,
  login: null,
  logout: null,
  accessToken: null,
  idApp: getIdApp(),
  inviteKey: getInviteKey(),
  inviteEmail: getEmail(),
  referer: referer(),
  refreshTimeoutId: null,
  isAuthenticated: false,
  isAuthLoading: true,
};

export const AuthContext = createContext<AuthContextProps>({
  AuthContextState: initialAuthContextState,
  handleLogIn: () => {},
  handleLogOut: () => {},
  redirectToApp: () => {},
  checkIsAuthenticated: () => {},
  refreshToken: async () => {},
  updateAuthTokens: async () => {},
  setAuthContextState: () => {},
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [AuthContextState, setAuthContextState] = useState<AuthContextState>(initialAuthContextState);
  const [csrfRefreshTimeoutId, setCsrfRefreshTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const resumeRefreshInProgressRef = useRef(false);

  const clearStoredAuth = () => {
    localStorage.removeItem("accessTokenExpAt");
    localStorage.removeItem("refreshTokenExpAt");
  };

  const scheduleCsrfRefresh = (ms: number) => {
    if (csrfRefreshTimeoutId) {
      clearTimeout(csrfRefreshTimeoutId);
    }

    const timeoutId = setTimeout(() => {
      fetchCsrfToken(setAuthContextState, scheduleCsrfRefresh);
    }, ms);
    setCsrfRefreshTimeoutId(timeoutId);
  };

  const scheduleTokenRefresh = () => {
    setAuthContextState((prevState) => {
      if (prevState.refreshTimeoutId !== null) {
        clearTimeout(prevState.refreshTimeoutId);
      }

      return {
        ...prevState,
        refreshTimeoutId: null,
      };
    });

    const expirationTimeStr = localStorage.getItem("accessTokenExpAt");
    if (!expirationTimeStr) return;

    const refreshAt = new Date(expirationTimeStr).getTime() - 3 * 60 * 1000;
    const timeUntilRefresh = refreshAt - Date.now();

    if (timeUntilRefresh <= 0) {
      refreshToken().catch((error) => console.error("Error token refresh:", error));
      return;
    }

    const timeoutId = setTimeout(() => {
      refreshToken().catch((error) => console.error("Error token refresh:", error));
    }, timeUntilRefresh);

    setAuthContextState((prevState) => ({
      ...prevState,
      refreshTimeoutId: timeoutId,
    }));
  };

  const applyAuthenticatedState = (me: MeResponse) => {
    setAuthContextState((prevState) => ({
      ...prevState,
      user: { email: me.email, name: me.displayName, userId: me.userId },
      accessToken: null,
      isAuthenticated: true,
      isAuthLoading: false,
      referer: referer(),
      idApp: getIdApp(),
    }));
  };

  const checkIsAuthenticated = async () => {
    const me = await fetchMe();
    if (me) {
      applyAuthenticatedState(me);
      return;
    }

    try {
      await refreshToken();
    } catch {
      clearStoredAuth();
      setAuthContextState((prevState) => ({
        ...prevState,
        user: emptyUser,
        accessToken: null,
        isAuthenticated: false,
        isAuthLoading: false,
        referer: referer(),
        idApp: getIdApp(),
      }));
    }
  };

  const removeAuthCallbackParams = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("login");
    currentUrl.searchParams.delete("logout");
    window.history.replaceState({}, document.title, `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
  };

  const currentCallbackUrl = () => {
    const callback = new URL(window.location.href);
    callback.searchParams.delete("login");
    callback.searchParams.delete("logout");
    return callback.toString();
  };

  const completeAuthCallback = async (action: "login" | "logout", oneTimeToken: string) => {
    const requestConfig = generateRequestConfig(
      "post",
      "onetimetoken",
      {
        action,
        oneTimeToken,
      },
      {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
        "Accept-Language": navigator.language || "en",
      }
    );

    const response = await axios(requestConfig);

    if (action === "logout") {
      await handleLogOut({ logout: oneTimeToken });
      return;
    }

    const { accessToken, refreshToken, accessTokenExpAt, refreshTokenExpAt, urlCallback } = response.data;
    if (!accessToken || !refreshToken || !accessTokenExpAt || !refreshTokenExpAt) {
      throw new Error("Callback response does not contain access and refresh tokens");
    }

    await handleLogIn({
      login: oneTimeToken,
      accessToken,
      refreshToken,
      accessTokenExpAt,
      refreshTokenExpAt,
      urlCallback,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchCsrfToken(setAuthContextState, scheduleCsrfRefresh);

      const urlParams = new URLSearchParams(window.location.search);
      const loginToken = urlParams.get("login");
      const logoutToken = urlParams.get("logout");

      try {
        if (loginToken) {
          await completeAuthCallback("login", loginToken);
          removeAuthCallbackParams();
          return;
        }

        if (logoutToken) {
          await completeAuthCallback("logout", logoutToken);
          removeAuthCallbackParams();
          return;
        }

        await checkIsAuthenticated();
        scheduleTokenRefresh();
      } catch (error) {
        console.error("Auth callback error:", error);
        clearStoredAuth();
        removeAuthCallbackParams();
        setAuthContextState((prevState) => ({
          ...prevState,
          user: emptyUser,
          accessToken: null,
          isAuthenticated: false,
          isAuthLoading: false,
        }));
      }
    };

    fetchData();
  }, []);

  const updateAuthTokens = async (data: AuthTokenData) => {
    const { accessToken, refreshToken, accessTokenExpAt, refreshTokenExpAt } = data;
    if (!accessToken || !refreshToken || !accessTokenExpAt || !refreshTokenExpAt) {
      throw new Error("Token response does not contain access and refresh tokens");
    }

    // docs/security-and-platform-work-plan.md Этап 7 — the backend already
    // set the HttpOnly cookies on this same response; only the non-sensitive
    // expiry timestamps are kept here, for scheduling.
    localStorage.setItem("accessTokenExpAt", accessTokenExpAt.toString());
    localStorage.setItem("refreshTokenExpAt", refreshTokenExpAt.toString());

    const me = await fetchMe();
    if (me) {
      applyAuthenticatedState(me);
    }
    scheduleTokenRefresh();
  };

  const refreshToken = async () => {
    // docs/security-and-platform-work-plan.md Этап 7 — the refresh token is
    // an HttpOnly cookie the browser sends automatically; this code can no
    // longer read it beforehand to short-circuit an obviously-missing or
    // expired token, so it always attempts the call and lets the backend's
    // 401 drive the logout path below.
    const requestConfig = generateRequestConfig(
      "post",
      "refresh",
      {
        deviceId: getDeviceId(),
        appId: getIdApp(),
      },
      {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
        "Accept-Language": navigator.language || "en",
      }
    );

    try {
      const response = await axios(requestConfig);
      await updateAuthTokens(response.data);
    } catch (error) {
      clearStoredAuth();
      setAuthContextState((prevState) => ({
        ...prevState,
        user: emptyUser,
        accessToken: null,
        isAuthenticated: false,
        isAuthLoading: false,
        referer: referer(),
        idApp: getIdApp(),
      }));
      throw error;
    }
  };

  useEffect(() => {
    const refreshAfterResume = async () => {
      if (document.visibilityState !== "visible" || resumeRefreshInProgressRef.current) return;

      // docs/security-and-platform-work-plan.md Этап 7 — accessTokenExpAt
      // is the only client-visible signal left (non-sensitive, still
      // returned in the JSON body alongside the HttpOnly cookie); whether a
      // refresh token cookie actually exists can only be learned by asking.
      const expirationTimeStr = localStorage.getItem("accessTokenExpAt");
      const expirationTime = expirationTimeStr ? new Date(expirationTimeStr).getTime() : Number.NaN;
      const shouldRefresh =
        !Number.isFinite(expirationTime) ||
        expirationTime <= Date.now() + 3 * 60 * 1000;

      if (!shouldRefresh) {
        scheduleTokenRefresh();
        return;
      }

      resumeRefreshInProgressRef.current = true;
      try {
        await refreshToken();
      } catch (error) {
        console.error("Error token refresh after tab resume:", error);
      } finally {
        resumeRefreshInProgressRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshAfterResume();
    };
    const handleFocus = () => void refreshAfterResume();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const redirectToApp = () => {
    const { login, logout } = AuthContextState;
    const ref = referer();

    if (ref) {
      if (login) {
        window.location.href = `${ref}?login=${login}`;
      }
      if (logout) {
        window.location.href = `${ref}?logout=${logout}`;
      }
    }
  };

  const handleLogIn = async (data: LoginData) => {
    const { accessToken, refreshToken, accessTokenExpAt, refreshTokenExpAt, login } = data;

    if (accessToken && refreshToken && login) {
      // docs/security-and-platform-work-plan.md Этап 7 — the backend already
      // set the HttpOnly cookies on this same response; only the expiry
      // timestamps are kept for scheduling. Who's logged in comes from /me.
      localStorage.setItem("accessTokenExpAt", accessTokenExpAt.toString());
      localStorage.setItem("refreshTokenExpAt", refreshTokenExpAt.toString());

      const me = await fetchMe();
      const callbackUrl = referer() || currentCallbackUrl();

      setAuthContextState((prevState) => ({
        ...prevState,
        login,
        accessToken: null,
        user: me ? { email: me.email, name: me.displayName, userId: me.userId } : emptyUser,
        isAuthenticated: Boolean(me),
        isAuthLoading: false,
        referer: callbackUrl,
      }));

      scheduleTokenRefresh();
    }
  };

  const handleLogOut = async (data: LogoutData) => {
    // docs/security-and-platform-work-plan.md Этап 7 — an HttpOnly cookie
    // can only be cleared by the backend; nothing left here for JS to remove.
    clearStoredAuth();

    const logout = data.logout;
    const callbackUrl = referer() || currentCallbackUrl();

    setAuthContextState((prevState) => ({
      ...prevState,
      user: emptyUser,
      accessToken: null,
      logout: logout ?? null,
      isAuthenticated: false,
      isAuthLoading: false,
      referer: callbackUrl,
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        AuthContextState,
        checkIsAuthenticated,
        redirectToApp,
        handleLogIn,
        handleLogOut,
        refreshToken,
        updateAuthTokens,
        setAuthContextState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
